import { useState, useRef, useEffect } from 'react';
import { Bold, Italic, List, ListOrdered, Heading2, Minus } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

/**
 * MarkdownEditor - A textarea with markdown formatting support
 * 
 * Features:
 * - Preserves line breaks and formatting
 * - Toolbar for common formatting
 * - Shift+Enter for new line (Enter also works)
 * - Auto-grows with content
 */
const MarkdownEditor = ({ 
  value, 
  onChange, 
  placeholder = "Enter description...",
  className,
  minRows = 6,
  maxRows = 20,
  ...props 
}) => {
  const textareaRef = useRef(null);
  const [focused, setFocused] = useState(false);

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const lineHeight = 24; // approximate line height
      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [value, minRows, maxRows]);

  const insertFormatting = (before, after = '', placeholder = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end) || placeholder;
    
    const newText = 
      value.substring(0, start) + 
      before + selectedText + after + 
      value.substring(end);
    
    onChange(newText);
    
    // Set cursor position after formatting
    setTimeout(() => {
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length, 
        start + before.length + selectedText.length
      );
    }, 0);
  };

  const handleBold = () => insertFormatting('**', '**', 'bold text');
  const handleItalic = () => insertFormatting('*', '*', 'italic text');
  const handleHeading = () => insertFormatting('\n### ', '\n', 'Section Title');
  const handleBulletList = () => insertFormatting('\n- ', '', 'list item');
  const handleNumberedList = () => insertFormatting('\n1. ', '', 'list item');
  const handleDivider = () => insertFormatting('\n---\n', '', '');

  const handleKeyDown = (e) => {
    // Allow natural Enter behavior for new lines
    // Shift+Enter also works for new line
    if (e.key === 'Tab') {
      e.preventDefault();
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      const newValue = value.substring(0, start) + '  ' + value.substring(end);
      onChange(newValue);
      setTimeout(() => {
        e.target.selectionStart = e.target.selectionEnd = start + 2;
      }, 0);
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {/* Toolbar */}
      <div className={cn(
        "flex flex-wrap gap-1 p-1.5 bg-muted/50 border border-border rounded-sm transition-colors",
        focused && "border-primary/50"
      )}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBold}
          className="h-7 w-7 p-0"
          title="Bold (Ctrl+B)"
        >
          <Bold size={14} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleItalic}
          className="h-7 w-7 p-0"
          title="Italic (Ctrl+I)"
        >
          <Italic size={14} />
        </Button>
        <div className="w-px h-5 bg-border mx-1 self-center" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleHeading}
          className="h-7 w-7 p-0"
          title="Section Heading"
        >
          <Heading2 size={14} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBulletList}
          className="h-7 w-7 p-0"
          title="Bullet List"
        >
          <List size={14} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleNumberedList}
          className="h-7 w-7 p-0"
          title="Numbered List"
        >
          <ListOrdered size={14} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDivider}
          className="h-7 w-7 p-0"
          title="Horizontal Divider"
        >
          <Minus size={14} />
        </Button>
        <span className="ml-auto text-xs text-muted-foreground self-center pr-2">
          Markdown supported
        </span>
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        className={cn(
          "w-full p-3 bg-transparent border border-border rounded-sm resize-none",
          "focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50",
          "placeholder:text-muted-foreground text-sm leading-relaxed",
          "font-mono"
        )}
        style={{ minHeight: `${minRows * 24}px` }}
        {...props}
      />

      {/* Helper text */}
      <p className="text-xs text-muted-foreground">
        Use **bold**, *italic*, ### headings, - bullets, 1. numbered lists. Line breaks are preserved.
      </p>
    </div>
  );
};

export { MarkdownEditor };
